import { useEffect, useState } from "react";

import { AI_TASKS, type TaskName } from "@oracus/shared/ai-tasks";
import type {
  ProviderSummary,
  TaskResolution,
} from "@oracus/shared/api/settings";

import {
  useGithubIntegration,
  useSyncGithub,
  useUpsertGithubIntegration,
} from "../api/github.api.ts";
import {
  useClearAssignment,
  useCreateProvider,
  useDeleteProvider,
  useProviderModels,
  useSettingsStatus,
  useUpsertAssignment,
} from "../api/settings.api.ts";
import { Badge, Button, Card, Page } from "../components/Page.tsx";

const KIND_OPTIONS = [
  { value: "openai", label: "OpenAI" },
  { value: "anthropic", label: "Anthropic" },
  { value: "google", label: "Google" },
  { value: "ollama", label: "Ollama" },
  { value: "openai_compatible", label: "OpenAI-compatible" },
] as const;

type KindValue = (typeof KIND_OPTIONS)[number]["value"];

export function Settings() {
  const status = useSettingsStatus();
  const create = useCreateProvider();
  const remove = useDeleteProvider();

  const [kind, setKind] = useState<KindValue>("openai");
  const [apiKey, setApiKey] = useState("");
  const [baseUrl, setBaseUrl] = useState("");

  const baseUrlPlaceholder =
    kind === "ollama"
      ? "http://localhost:11434/api"
      : kind === "openai_compatible"
        ? "http://localhost:1234/v1"
        : "";

  return (
    <Page
      title="Settings"
      description="AI providers and per-task assignment. Env vars are honored as a fallback when the database has no provider rows."
    >
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card title="Configuration health">
          {status.isLoading && (
            <p className="text-sm text-zinc-500">Loading…</p>
          )}
          {status.data && (
            <div className="space-y-4">
              {(() => {
                const total = status.data.taskResolutions.length;
                const resolved = status.data.taskResolutions.filter(
                  (t) => t.resolved,
                ).length;
                const allOk = resolved === total;
                return (
                  <div className="text-sm">
                    <Badge tone={allOk ? "success" : "warn"}>
                      {allOk
                        ? `All ${total} tasks configured`
                        : `${resolved}/${total} tasks configured`}
                    </Badge>
                  </div>
                );
              })()}
              <div className="text-sm">
                <span className="text-zinc-500">DB providers:</span>{" "}
                <Badge
                  tone={status.data.providers.length > 0 ? "success" : "neutral"}
                >
                  {status.data.providers.length}
                </Badge>{" "}
                <span className="text-zinc-500">Env fallback:</span>{" "}
                <Badge tone={status.data.envKeys.openai ? "success" : "neutral"}>
                  OpenAI {status.data.envKeys.openai ? "set" : "—"}
                </Badge>{" "}
                <Badge
                  tone={status.data.envKeys.anthropic ? "success" : "neutral"}
                >
                  Anthropic {status.data.envKeys.anthropic ? "set" : "—"}
                </Badge>
              </div>
              <div className="text-sm">
                <span className="text-zinc-500">Encryption:</span>{" "}
                <Badge tone={status.data.encryptionConfigured ? "success" : "warn"}>
                  {status.data.encryptionConfigured
                    ? "configured"
                    : "ENCRYPTION_KEY missing"}
                </Badge>
              </div>
              <div>
                <div className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                  Task resolution
                </div>
                <ul className="mt-2 divide-y divide-zinc-100">
                  {status.data.taskResolutions.map((t) => (
                    <li
                      key={t.task}
                      className="flex items-center justify-between py-2 text-sm"
                    >
                      <span className="font-mono">{t.task}</span>
                      {t.resolved ? (
                        <span className="flex items-center gap-2">
                          <Badge
                            tone={t.source === "db" ? "success" : "info"}
                          >
                            {t.source}
                          </Badge>
                          <span className="text-xs text-zinc-600">
                            {t.providerKind}:{t.model}
                          </span>
                        </span>
                      ) : (
                        <Badge tone="danger">unconfigured</Badge>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </Card>

        <Card title="Add a provider">
          <p className="text-sm text-zinc-600">
            Stored encrypted at rest. Adding a provider auto-assigns it to compatible tasks where no DB assignment yet exists. Self-hosted (Ollama / OpenAI-compatible) needs explicit assignment below.
          </p>
          <div className="mt-4 space-y-3">
            <label className="block text-sm">
              <span className="text-zinc-700">Kind</span>
              <select
                value={kind}
                onChange={(e) => setKind(e.target.value as KindValue)}
                className="mt-1 block w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm"
              >
                {KIND_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              <span className="text-zinc-700">
                API key{" "}
                {kind === "ollama" && (
                  <span className="text-xs text-zinc-500">(optional for local Ollama)</span>
                )}
              </span>
              <input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={kind === "ollama" ? "leave empty for local" : "sk-..."}
                className="mt-1 block w-full rounded-md border border-zinc-300 bg-white px-3 py-2 font-mono text-sm"
              />
            </label>
            {(kind === "ollama" || kind === "openai_compatible") && (
              <label className="block text-sm">
                <span className="text-zinc-700">Base URL</span>
                <input
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  placeholder={baseUrlPlaceholder}
                  className="mt-1 block w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm"
                />
              </label>
            )}
            <Button
              onClick={() => {
                create.mutate(
                  {
                    kind,
                    ...(apiKey ? { apiKey } : {}),
                    ...(baseUrl ? { baseUrl } : {}),
                  },
                  {
                    onSuccess: () => {
                      setApiKey("");
                      setBaseUrl("");
                    },
                  },
                );
              }}
              disabled={
                create.isPending ||
                (kind !== "ollama" && !apiKey.trim()) ||
                ((kind === "ollama" || kind === "openai_compatible") &&
                  !baseUrl.trim())
              }
            >
              {create.isPending ? "Saving…" : "Add provider"}
            </Button>
            {create.error && (
              <p className="text-xs text-red-700">
                {String((create.error as Error).message)}
              </p>
            )}
          </div>
        </Card>
      </div>

      <div className="mt-6">
        <Card title="Task assignments">
          <p className="text-sm text-zinc-600">
            Pick which provider and model handles each task. Required for self-hosted providers since Oracus can't guess what you're running.
          </p>
          <ul className="mt-4 divide-y divide-zinc-100">
            {(Object.keys(AI_TASKS) as TaskName[]).map((task) => {
              const resolution = status.data?.taskResolutions.find(
                (r) => r.task === task,
              );
              return (
                <TaskAssignmentRow
                  key={task}
                  task={task}
                  resolution={resolution}
                  providers={status.data?.providers ?? []}
                />
              );
            })}
          </ul>
        </Card>
      </div>

      <div className="mt-6">
        <Card title="Configured providers">
          {status.data?.providers.length === 0 && (
            <p className="text-sm text-zinc-500">
              None yet. Env-fallback is in effect for tasks where the matching env key is set.
            </p>
          )}
          <ul className="divide-y divide-zinc-100">
            {(status.data?.providers ?? []).map((p) => (
              <li
                key={p.id}
                className="flex items-center justify-between py-2.5 text-sm"
              >
                <span className="flex items-center gap-2">
                  <Badge tone="info">{p.kind}</Badge>
                  <span>{p.displayName}</span>
                  {p.hasApiKey && <Badge tone="success">key</Badge>}
                  {p.baseUrl && (
                    <span className="text-xs text-zinc-500">{p.baseUrl}</span>
                  )}
                </span>
                <Button
                  variant="danger"
                  onClick={() => {
                    if (confirm(`Delete ${p.displayName}?`))
                      remove.mutate(p.id);
                  }}
                >
                  Delete
                </Button>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      <div className="mt-6">
        <GithubIntegrationCard />
      </div>

    </Page>
  );
}

function GithubIntegrationCard() {
  const status = useGithubIntegration();
  const save = useUpsertGithubIntegration();
  const sync = useSyncGithub();

  const [orgLogin, setOrgLogin] = useState("");
  const [pat, setPat] = useState("");
  const [hydrated, setHydrated] = useState(false);

  // Seed the form once when status arrives; don't keep clobbering local edits
  // afterwards. The PAT field stays blank — we never show the existing PAT,
  // even masked, since it's only on disk encrypted.
  useEffect(() => {
    if (!hydrated && status.data) {
      setOrgLogin(status.data.orgLogin ?? "");
      setHydrated(true);
    }
  }, [hydrated, status.data]);

  const lastResult = sync.data;
  const canSync =
    !!status.data?.configured &&
    !!status.data.hasPat &&
    !sync.isPending &&
    !save.isPending;

  return (
    <Card title="GitHub integration">
      <p className="text-sm text-zinc-600">
        Source of truth for teams. Configure a GitHub org and a PAT with{" "}
        <code className="rounded bg-zinc-100 px-1 text-[11px]">read:org</code>{" "}
        scope, then sync to mirror teams, members, and repo ownership into
        Oracus. The PAT is stored encrypted at rest.
      </p>

      {status.data && (
        <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
          <span className="text-zinc-500">Org:</span>
          <Badge tone={status.data.orgLogin ? "info" : "neutral"}>
            {status.data.orgLogin ?? "—"}
          </Badge>
          <span className="text-zinc-500">PAT:</span>
          <Badge tone={status.data.hasPat ? "success" : "warn"}>
            {status.data.hasPat ? "stored" : "missing"}
          </Badge>
          <span className="text-zinc-500">Status:</span>
          <Badge
            tone={
              status.data.syncStatus === "error"
                ? "danger"
                : status.data.syncStatus === "syncing"
                  ? "info"
                  : "neutral"
            }
          >
            {status.data.syncStatus}
          </Badge>
          {status.data.lastSyncedAt && (
            <>
              <span className="text-zinc-500">Last synced:</span>
              <span className="text-xs text-zinc-700">
                {new Date(status.data.lastSyncedAt).toLocaleString()}
              </span>
            </>
          )}
        </div>
      )}

      {status.data?.syncError && (
        <p className="mt-2 rounded-md bg-red-50 px-3 py-2 text-xs text-red-800">
          {status.data.syncError}
        </p>
      )}

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="text-zinc-700">Organization login</span>
          <input
            value={orgLogin}
            onChange={(e) => setOrgLogin(e.target.value)}
            placeholder="acme-inc"
            className="mt-1 block w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm"
          />
        </label>
        <label className="block text-sm">
          <span className="text-zinc-700">
            Personal access token{" "}
            <span className="text-xs text-zinc-500">
              (leave blank to keep the stored one)
            </span>
          </span>
          <input
            type="password"
            value={pat}
            onChange={(e) => setPat(e.target.value)}
            placeholder={
              status.data?.hasPat ? "•••••••• (unchanged)" : "ghp_…"
            }
            className="mt-1 block w-full rounded-md border border-zinc-300 bg-white px-3 py-2 font-mono text-sm"
          />
        </label>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button
          onClick={() => {
            save.mutate(
              {
                orgLogin: orgLogin.trim(),
                ...(pat ? { pat } : {}),
              },
              { onSuccess: () => setPat("") },
            );
          }}
          disabled={!orgLogin.trim() || save.isPending}
        >
          {save.isPending ? "Saving…" : "Save"}
        </Button>
        <Button
          variant="secondary"
          onClick={() => sync.mutate()}
          disabled={!canSync}
        >
          {sync.isPending ? "Syncing…" : "Sync now"}
        </Button>
        {save.error && (
          <span className="text-xs text-red-700">
            {String((save.error as Error).message)}
          </span>
        )}
        {sync.error && (
          <span className="text-xs text-red-700">
            {String((sync.error as Error).message)}
          </span>
        )}
      </div>

      {lastResult && (
        <div className="mt-3 rounded-md bg-zinc-50 px-3 py-2 text-xs text-zinc-700">
          Synced <b>{lastResult.teamsSynced}</b> team
          {lastResult.teamsSynced === 1 ? "" : "s"} ·{" "}
          <b>{lastResult.usersUpserted}</b> member upsert
          {lastResult.usersUpserted === 1 ? "" : "s"} ·{" "}
          <b>{lastResult.reposLinked}</b> repo
          {lastResult.reposLinked === 1 ? "" : "s"} linked
          {lastResult.reposCleared > 0 && (
            <>
              {" "}
              · <b>{lastResult.reposCleared}</b> cleared
            </>
          )}
          {lastResult.teamsRemoved > 0 && (
            <>
              {" "}
              · <b>{lastResult.teamsRemoved}</b> removed
            </>
          )}
        </div>
      )}
    </Card>
  );
}

function TaskAssignmentRow({
  task,
  resolution,
  providers,
}: {
  task: TaskName;
  resolution: TaskResolution | undefined;
  providers: ProviderSummary[];
}) {
  const def = AI_TASKS[task];
  const upsert = useUpsertAssignment();
  const clear = useClearAssignment();

  const dbProviderId =
    resolution?.source === "db" ? resolution.providerId ?? "" : "";
  const dbModel =
    resolution?.source === "db" ? resolution.model ?? "" : "";
  const dbDimensions =
    resolution?.source === "db" && task === "embed"
      ? typeof resolution.params?.dimensions === "number"
        ? resolution.params.dimensions
        : undefined
      : undefined;

  const [providerId, setProviderId] = useState<string>(dbProviderId);
  const [model, setModel] = useState<string>(dbModel);
  const [dimensions, setDimensions] = useState<string>(
    dbDimensions !== undefined ? String(dbDimensions) : "",
  );

  // Re-sync local state when the resolution changes (after a save or another tab edit).
  useEffect(() => {
    setProviderId(dbProviderId);
    setModel(dbModel);
    setDimensions(dbDimensions !== undefined ? String(dbDimensions) : "");
  }, [dbProviderId, dbModel, dbDimensions]);

  const selectedProvider = providers.find((p) => p.id === providerId);
  const suggestedModel =
    selectedProvider &&
    (def.defaults as Record<string, { model: string } | null | undefined>)[
      selectedProvider.kind
    ]?.model;

  const models = useProviderModels(providerId);
  const datalistId = `models-${task}`;

  const canSave =
    providerId.length > 0 && model.trim().length > 0 && !upsert.isPending;

  return (
    <li className="grid grid-cols-1 gap-3 py-3 md:grid-cols-[1fr_2fr] md:items-start">
      <div>
        <div className="font-mono text-sm">{task}</div>
        <div className="text-xs text-zinc-500">{def.description}</div>
        <div className="mt-1">
          {resolution?.resolved ? (
            <Badge tone={resolution.source === "db" ? "success" : "info"}>
              {resolution.source}: {resolution.providerKind}:{resolution.model}
            </Badge>
          ) : (
            <Badge tone="danger">unconfigured</Badge>
          )}
        </div>
      </div>
      <div className="space-y-2">
        <div className="flex flex-col gap-2 sm:flex-row">
          <select
            value={providerId}
            onChange={(e) => {
              const next = e.target.value;
              setProviderId(next);
              const prov = providers.find((p) => p.id === next);
              if (prov && !model.trim()) {
                const sug = (
                  def.defaults as Record<
                    string,
                    { model: string } | null | undefined
                  >
                )[prov.kind]?.model;
                if (sug) setModel(sug);
              }
            }}
            className="block w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm sm:w-1/2"
          >
            <option value="">— pick a provider —</option>
            {providers.map((p) => (
              <option key={p.id} value={p.id}>
                {p.displayName} ({p.kind})
              </option>
            ))}
          </select>
          <input
            list={datalistId}
            value={model}
            onChange={(e) => setModel(e.target.value)}
            placeholder={suggestedModel ?? "model name"}
            className="block w-full rounded-md border border-zinc-300 bg-white px-3 py-2 font-mono text-sm sm:w-1/2"
          />
          {models.data && (
            <datalist id={datalistId}>
              {models.data.models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label !== m.id ? m.label : ""}
                </option>
              ))}
            </datalist>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-zinc-600">
          <button
            type="button"
            disabled={!providerId || models.isFetching}
            onClick={() => void models.refetch()}
            className="rounded border border-zinc-300 bg-white px-2 py-1 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {models.isFetching ? "Listing…" : "List models from provider"}
          </button>
          {models.data && (
            <span className="text-emerald-700">
              ✓ {models.data.models.length} model
              {models.data.models.length === 1 ? "" : "s"} reachable
            </span>
          )}
          {models.error && (
            <span className="text-red-700">
              {String((models.error as Error).message)}
            </span>
          )}
        </div>
        {task === "embed" && (
          <label className="block text-xs text-zinc-600">
            <span>Dimensions (optional, for Matryoshka-truncated models)</span>
            <input
              value={dimensions}
              onChange={(e) =>
                setDimensions(e.target.value.replace(/[^0-9]/g, ""))
              }
              placeholder="1024"
              className="mt-1 block w-32 rounded-md border border-zinc-300 bg-white px-3 py-1.5 font-mono text-sm"
            />
          </label>
        )}
        <div className="flex gap-2">
          <Button
            disabled={!canSave}
            onClick={() => {
              const dim =
                task === "embed" && dimensions.trim()
                  ? Number(dimensions)
                  : undefined;
              upsert.mutate({
                task,
                providerId,
                model: model.trim(),
                ...(dim !== undefined ? { params: { dimensions: dim } } : {}),
              });
            }}
          >
            {upsert.isPending ? "Saving…" : "Save"}
          </Button>
          {resolution?.source === "db" && (
            <Button
              variant="secondary"
              disabled={clear.isPending}
              onClick={() => {
                if (confirm(`Clear DB assignment for '${task}'? Will fall back to env if available.`)) {
                  clear.mutate(task);
                }
              }}
            >
              Clear
            </Button>
          )}
        </div>
        {upsert.error && (
          <p className="text-xs text-red-700">
            {String((upsert.error as Error).message)}
          </p>
        )}
      </div>
    </li>
  );
}
